/* eslint-disable camelcase */
import { UseChords as Chords } from '../use-fetch-chords'
import { useTransposeKeyChord } from './use-simplify-chords'
import { ChordsDiagram, getDiagram } from './utils'

interface ChordsItem {
  key: string
  transposed?: string
  diagram?: ChordsDiagram | null
}

interface UseChords {
  previous: ChordsItem
  current: ChordsItem
  next: ChordsItem
}

export const getClosestTime = (list: number[], target: number): number =>
  list.reduce((prev: number, curr: number) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
  )

export const generateDiagram = (fullChord: any): any => {
  // regex to get first char of string and
  // ignore the rest if is't not a # char or b char or @ char
  const key = fullChord?.replace(/^(.)(b|#)?(.*)$/, '$1$2')
  let suffix = fullChord?.replace(/^(.)(b|#)?(.*)$/, '$3')
  suffix =
    suffix === ':min' || suffix === 'm'
      ? 'minor'
      : suffix === ':maj'
      ? 'major'
      : suffix
  const diagram = getDiagram(key, suffix)
  return diagram
}

export const getKey = (item: any, type1: string): string => {
  return (item && item[type1]) || ''
}

export const formatTransposed = (value: string): string => {
  const key = value.replace(/^(.)(b|#)?(.*)$/, '$1$2')
  let suffix = value?.replace(/^(.)(b|#)?(.*)$/, '$3')
  suffix =
    suffix === ':min' || suffix === 'm' ? 'm' : suffix === ':maj' ? '' : suffix
  return `${key}${suffix}`
}

export const useChords = ({
  shouldLock,
  pitch,
  songKey,
  chords,
  audioTime,
  loading,
  error,
  type = 'prev_chord'
}: {
  shouldLock: boolean
  pitch: number
  songKey?: string
  audioTime: number
  chords: Chords
  loading: boolean
  error: boolean
  type?: string
}): UseChords => {
  const { transpose } = useTransposeKeyChord()

  if (loading || error || !Array.isArray(chords) || !chords.length) {
    const empty = { key: '', transposed: '' }
    return {
      previous: empty,
      current: empty,
      next: empty
    }
  }

  let currentChordIndex = 0

  const limit = 60

  const time = shouldLock && audioTime > limit ? limit : audioTime

  const times = chords.map(
    ({ curr_beat_time }: { curr_beat_time: number }) => curr_beat_time
  )

  const closesTime = getClosestTime(times, time)

  const keyCurrent1 = getKey(
    chords.find(
      ({ curr_beat_time }: { curr_beat_time: number }, index: number) => {
        if (curr_beat_time !== closesTime) return false

        currentChordIndex = index
        return true
      }
    ),
    type
  )

  const current: ChordsItem = {
    key: keyCurrent1,
    transposed: transpose({
      currentKey: songKey,
      currentChord: keyCurrent1,
      pitch
    })
  }
  current.diagram = generateDiagram(current.transposed)
  current.transposed = formatTransposed(current.transposed || '')

  const keyPrevious1 = getKey(
    chords
      .slice(0, currentChordIndex)
      .filter((item: any) => item[type] !== current.key)
      .pop(),
    type
  )

  const previous: ChordsItem = {
    key: keyPrevious1,
    transposed: transpose({
      currentKey: songKey,
      currentChord: keyPrevious1,
      pitch
    })
  }

  previous.diagram = generateDiagram(previous.transposed)
  previous.transposed = formatTransposed(previous.transposed || '')

  const keyNext1 = getKey(
    chords.find((item: any, index: number) => {
      return index > currentChordIndex && item[type] !== current.key
    }),
    type
  )

  const next: ChordsItem = {
    key: keyNext1,
    transposed: transpose({
      currentKey: songKey,
      currentChord: keyNext1,
      pitch
    })
  }
  next.diagram = generateDiagram(next.transposed)
  next.transposed = formatTransposed(next.transposed || '')

  return {
    previous,
    current,
    next
  }
}
